import React from 'react';
import { colors, radii } from '../../theme/tokens';

// Fixed v1 statuses. `in_progress` is what drives time tracking on the server.
export const STATUSES = [
  { value: 'todo', label: 'To Do', color: colors.textMuted },
  { value: 'in_progress', label: 'In Progress', color: colors.parGreenBright },
  { value: 'blocked', label: 'Blocked', color: colors.amber },
  { value: 'done', label: 'Done', color: colors.blue }
];

export function statusMeta(value) {
  return STATUSES.find((s) => s.value === value) || STATUSES[0];
}

// Task priority. Setting a task to "high" also bumps it back to In Progress
// (enforced server-side). Colours: normal = green, medium = amber, high = red.
export const PRIORITIES = [
  { value: 'normal', label: 'Normal', color: colors.parGreenBright },
  { value: 'medium', label: 'Medium', color: colors.amber },
  { value: 'high', label: 'High', color: colors.red }
];

export function priorityMeta(value) {
  return PRIORITIES.find((p) => p.value === value) || PRIORITIES[0];
}

// A field is visible in a department when it has no department tags (shown
// everywhere) or its tags include that department's id.
export function fieldVisibleInDept(field, deptId) {
  if (!field.departments || field.departments.length === 0) return true;
  return field.departments.map(String).includes(String(deptId));
}

// The fields a task type effectively shows in a given department.
// For the built-in "General Task" (isDefault) it's the union of every field
// from the department's other task types, so you never have to build a template
// just to capture details. Normal types use their own dept-visible fields.
export function effectiveFields(taskType, deptId, allTaskTypes = []) {
  if (!taskType) return [];
  if (taskType.isDefault) {
    const seen = new Set();
    const out = [];
    allTaskTypes.forEach((t) => {
      if (t.isDefault) return;
      const appliesToDept = !t.departments?.length || t.departments.map(String).includes(String(deptId));
      if (!appliesToDept) return;
      (t.fields || []).forEach((f) => {
        if (!fieldVisibleInDept(f, deptId)) return;
        if (seen.has(f.key)) return;
        seen.add(f.key);
        out.push(f);
      });
    });
    return out.sort((a, b) => (a.order || 0) - (b.order || 0));
  }
  return (taskType.fields || [])
    .filter((f) => fieldVisibleInDept(f, deptId))
    .sort((a, b) => (a.order || 0) - (b.order || 0));
}

export function formatDuration(ms) {
  if (!ms || ms <= 0) return '0m';
  const totalMinutes = Math.round(ms / 60000);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

// Compact "days until deadline" badge info for the collapsed task row.
// Returns null when there's no deadline.
export function daysLeft(deadline) {
  if (!deadline) return null;
  const d = new Date(deadline);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  const a = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const b = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diff = Math.round((b - a) / 86400000);
  if (diff > 1) return { label: `${diff}d left`, color: colors.textMuted };
  if (diff === 1) return { label: 'Tomorrow', color: colors.amber };
  if (diff === 0) return { label: 'Today', color: colors.amber };
  return { label: `${Math.abs(diff)}d over`, color: colors.red };
}

// Normalize an id-or-populated-ref to a plain string id.
const idOf = (v) => String(v && v._id ? v._id : v || '');

// Count unread chat messages for a user on a task — but only when the user has
// a stake in it: assigned to the task, assigned to one of its checklist items,
// or @mentioned in the chat. Otherwise returns 0 (no indicator for bystanders).
// A message is unread when it's newer than the user's last read marker and not
// authored by them.
export function unreadForUser(task, userId) {
  if (!task || !userId) return 0;
  const me = String(userId);
  const messages = task.messages || [];

  const assignedToTask = (task.assignees || []).some((a) => idOf(a) === me);
  const assignedToTodo = (task.checklist || []).some((c) => idOf(c.assignee) === me);
  const mentioned = messages.some((m) => (m.mentions || []).some((x) => idOf(x) === me));
  if (!assignedToTask && !assignedToTodo && !mentioned) return 0;

  const read = (task.reads || []).find((r) => idOf(r.userId) === me);
  const lastRead = read ? new Date(read.at).getTime() : 0;
  return messages.filter((m) => idOf(m.userId) !== me && new Date(m.createdAt).getTime() > lastRead).length;
}

// Checklist completion for a task. total 0 means "no checklist on this task".
export function checklistProgress(task) {
  const items = (task && task.checklist) || [];
  const total = items.length;
  const done = items.filter((i) => i && i.done).length;
  return { total, done, pct: total ? Math.round((done / total) * 100) : 0 };
}

export function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '—';
  }
}

// Avatar bubble for a user object ({ name, picture }).
export const Avatar = ({ user, size = 26 }) => (
  <span
    title={user?.name}
    style={{
      width: size,
      height: size,
      borderRadius: '50%',
      overflow: 'hidden',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: colors.surface2,
      border: `1px solid ${colors.glassBorder}`,
      fontSize: size * 0.42,
      fontWeight: 700,
      color: colors.text,
      flexShrink: 0
    }}
  >
    {user?.picture ? (
      <img src={user.picture} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
    ) : (
      (user?.name || '?').charAt(0).toUpperCase()
    )}
  </span>
);

// Input control for a task-type field, used in the create form and detail edit.
export const FieldInput = ({ field, value, onChange, users = [] }) => {
  const base = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: radii.sm,
    border: `1px solid ${colors.border}`,
    background: colors.inputBg,
    color: colors.text,
    fontSize: 14,
    boxSizing: 'border-box',
    fontFamily: 'inherit'
  };

  if (field.type === 'textarea') {
    return <textarea style={{ ...base, minHeight: 70, resize: 'vertical' }} value={value || ''} onChange={(e) => onChange(e.target.value)} />;
  }
  if (field.type === 'number') {
    return <input type="number" style={base} value={value ?? ''} onChange={(e) => onChange(e.target.value)} />;
  }
  if (field.type === 'date') {
    return <input type="date" style={base} value={value ? String(value).slice(0, 10) : ''} onChange={(e) => onChange(e.target.value)} />;
  }
  if (field.type === 'select') {
    return (
      <select style={base} value={value || ''} onChange={(e) => onChange(e.target.value)}>
        <option value="">— Select —</option>
        {(field.options || []).map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    );
  }
  if (field.type === 'user') {
    return (
      <select style={base} value={value || ''} onChange={(e) => onChange(e.target.value)}>
        <option value="">— Select user —</option>
        {users.map((u) => (
          <option key={u._id} value={u._id}>{u.name}</option>
        ))}
      </select>
    );
  }
  return <input type="text" style={base} value={value || ''} onChange={(e) => onChange(e.target.value)} />;
};
